// pages/AppointmentBooking.js
//Called from ProviderProfilePage: To allow patients book appointment
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useLocation, useNavigate, Link } from "react-router-dom";
import {
  getAvailabilityById,
  getAvailabilityInfoByStart,
  getSlotsByDate,
} from "../APIs/availability"; // 👈 added getAvailabilityInfoByStart
import { createPatientInsurance } from "../APIs/patientInsuranceApi";
import { getInsurancePlans } from "../APIs/dropdownApi";
import { listPatientInsurances } from "../APIs/patientInsuranceApi";
import {
  bookAppointmentFromSlot,
  checkAppointmentConflict,
} from "../APIs/appointmentsApi";
import {
  getAcceptingInsurance,
  getProviderPricing,
  createDepositSession,
} from "../APIs/providerApi";
import AppointmentConflictModal from "../Patient/AppointmentConflictModal";
import CompletePatientProfileModal from "../Patient/CompletePatientProfileModal";
import { fetchCurrentUser } from "../APIs/authApi";
import { canSubmitBooking } from "./bookingValidation";
import keycloak from "../APIs/keycloak";

// Default to the user's system timezone
const defaultZone = (() => {
  try {
    return (
      Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York"
    );
  } catch {
    return "America/New_York";
  }
})();

// --- formatting helpers ---
function fmtDate(dateStrOrZdt) {
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStrOrZdt)) {
    const [y, m, d] = dateStrOrZdt.split("-").map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
    return dt.toLocaleDateString([], {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }
  const iso = String(dateStrOrZdt).split("[")[0];
  const dt = new Date(iso);
  return dt.toLocaleDateString([], {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}
function fmtTime(zdtString) {
  const isoWithOffset = String(zdtString).split("[")[0];
  const d = new Date(isoWithOffset);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export default function AppointmentBooking({ patientId: patientIdProp }) {
  //Use the availabilityId from the here: info.availabilityId if you absolutely need to
  //After you get it from:data = await getAvailabilityInfoByStart(Number(providerId), { start: startZdt, zone });
  /*const { providerId, availabilityId, patientId } = useParams();
  const { search } = useLocation();
  const qs = new URLSearchParams(search);
  const startZdtRaw = qs.get('start');*/

  const navigate = useNavigate();

  const { patientId: patientIdParam } = useParams();
  const [resolvedPatientId, setResolvedPatientId] = useState(null);

  const { providerId } = useParams();
  const { search } = useLocation();
  const qs = new URLSearchParams(search);
  const startZdtRaw = qs.get("start");
  // If you need availabilityId, get it from query params instead:
  const availabilityId = qs.get("availabilityId");

  //const startZdt = qs.get('start'); // e.g. "2025-09-19T11:00:00-04:00[America/New_York]"
  const startZdt = React.useMemo(() => {
    if (!startZdtRaw) return null;
    // decode then strip anything accidentally appended like "?providerName=..."
    const decoded = decodeURIComponent(startZdtRaw);
    return decoded.split("?")[0];
  }, [startZdtRaw]);
  const providerName = qs.get("providerName") || "";
  const specialty = qs.get("specialty") || "";
  const facility = qs.get("facility") || "";

  const [zone] = useState(defaultZone);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState(null); // the availability payload

  //Start and end time
  const [slotStartOverride, setSlotStartOverride] = useState(null);
  const [slotEndOverride, setSlotEndOverride] = useState(null);
  const [slotInfo, setSlotInfo] = useState(null); // { slotId, availabilityId, slotStart, slotEnd, telehealth, status }

  function sameInstant(a, b) {
    const ta = new Date(String(a).split("[")[0]).getTime();
    const tb = new Date(String(b).split("[")[0]).getTime();
    return ta === tb;
  }

  async function resolveSlotOrThrow({ providerId, slotStartZdt, zone }) {
    const iso = String(slotStartZdt).split("[")[0];
    const d = new Date(iso);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const date = `${yyyy}-${mm}-${dd}`;

    // IMPORTANT: don't force status=FREE here — you want the real status back.
    const slots = await getSlotsByDate(Number(providerId), { date, zone });

    const match = (slots || []).find((s) =>
      sameInstant(s.slotStart, slotStartZdt),
    );
    if (!match) throw new Error("Slot not found for this start time.");
    return match; // { slotId, availabilityId, slotStart, slotEnd, telehealth, status }
  }

  // Conflict detection state
  const [conflictData, setConflictData] = useState(null); // { conflicts, pendingSlot }
  const [proceedingAfterConflict, setProceedingAfterConflict] = useState(false);

  // Gating state (same pattern as ProviderMatchQuestions)
  const [currentUser, setCurrentUser] = useState(null);
  const [showCompleteProfileModal, setShowCompleteProfileModal] =
    useState(false);
  const token = keycloak?.token;

  // currentUser.patientId is the authoritative fallback when the route param is absent
  const patientId =
    patientIdProp ??
    patientIdParam ??
    resolvedPatientId ??
    currentUser?.patientId ??
    null;

  // 🔹 Load current user for BASIC_ACCOUNT vs COMPLETED_PROFILE
  useEffect(() => {
    (async () => {
      try {
        const me = await fetchCurrentUser();
        setCurrentUser(me); // null if unauthenticated
      } catch (e) {
        console.error("Failed to fetch current user for booking gating:", e);
        setCurrentUser(null);
      }
    })();
  }, []);

  // --- Insurance UI state ---
  const [insLoading, setInsLoading] = useState(false);
  const [insError, setInsError] = useState("");
  const [plans, setPlans] = useState([]); // [{ planId, planName, planTypes:[{id,typeName}] }, ...]
  const [useInsurance, setUseInsurance] = useState(true); // collapsible selector (default to "with insurance")

  // Form fields
  const [planId, setPlanId] = useState("");
  const [planTypeId, setPlanTypeId] = useState(""); // UI-only for now
  const [appointmentReason, setAppointmentReason] = useState(""); // 👈 ADD
  const [memberId, setMemberId] = useState("");
  const [groupId, setGroupId] = useState("");
  const [coverageStart, setCoverageStart] = useState("");
  const [coverageEnd, setCoverageEnd] = useState("");
  const [status, setStatus] = useState("PENDING"); // PENDING | VERIFIED | REJECTED

  // --- Self-pay pricing ---
  const [pricingRows, setPricingRows] = useState([]);
  const [depositPending, setDepositPending] = useState(false);
  const [acceptingInsurance, setAcceptingInsurance] = useState(null);
  const [insuranceSettingError, setInsuranceSettingError] = useState("");

  useEffect(() => {
    if (!providerId) return;
    Promise.all([
      getProviderPricing(providerId),
      getAcceptingInsurance(providerId),
    ])
      .then(([rows, insuranceSetting]) => {
        setPricingRows(Array.isArray(rows) ? rows : []);
        setAcceptingInsurance(insuranceSetting.acceptingInsurance);
        setInsuranceSettingError("");
        if (!insuranceSetting.acceptingInsurance) setUseInsurance(false);
      })
      .catch(() => {
        setPricingRows([]);
        setAcceptingInsurance(null);
        setInsuranceSettingError("Unable to load provider payment options.");
      });
  }, [providerId]);

  // derived types for selected plan
  const selectedPlan = useMemo(
    () => plans.find((p) => p.planId === planId),
    [plans, planId],
  );
  const planTypeOptions = selectedPlan?.planTypes || [];

  const matchedPricing = useMemo(() => {
    const slotType = info?.appointmentType;
    if (!slotType) return null;
    return pricingRows.find((row) => row.appointmentType === slotType) || null;
  }, [info?.appointmentType, pricingRows]);

  // Gate the Book button on mandatory fields
  const canBook = useMemo(() => {
    return canSubmitBooking({
      appointmentReason,
      useInsurance,
      planId,
      planTypeId,
      memberId,
      acceptingInsurance,
      selfPayPricingAvailable: Boolean(matchedPricing),
    });
  }, [
    appointmentReason,
    useInsurance,
    planId,
    planTypeId,
    memberId,
    acceptingInsurance,
    matchedPricing,
  ]);

  const fetchInfo = async () => {
    setLoading(true);
    setErr("");
    try {
      /*let data;
      if (availabilityId) {
        // Load by ID (preferred when present)
        data = await getAvailabilityById(
          Number(providerId),
          Number(availabilityId),
          { zone },
        );
      } else if (startZdt) {
        // Fallback: load by start ZonedDateTime (when we only have ?start=...)
        data = await getAvailabilityInfoByStart(Number(providerId), {
          start: startZdt,
          zone,
        });

      } else {
        throw new Error("Missing availabilityId or start time");
      }
      setInfo(data);*/

      let availability;
      if (availabilityId) {
        availability = await getAvailabilityById(
          Number(providerId),
          Number(availabilityId),
          { zone },
        );
      } else {
        availability = await getAvailabilityInfoByStart(Number(providerId), {
          start: startZdt,
          zone,
        });
      }
      setInfo(availability);

      // Resolve slot end for display (slot-level), based on startZdt
      try {
        /*const slotStartZdt = startZdt || data.startDateTime;
        if (slotStartZdt) {
          const { date: slotDate } = (() => {
            const iso = String(slotStartZdt).split("[")[0];
            const d = new Date(iso);
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, "0");
            const dd = String(d.getDate()).padStart(2, "0");
            return { date: `${yyyy}-${mm}-${dd}` };
          })();

          const slots = await getSlotsByDate(Number(providerId), {
            date: slotDate,
            zone,
            status: "FREE",
          });


          const match = (slots || []).find(
            (s) => String(s.slotStart) === String(slotStartZdt),
          );

          setSlotStartOverride(match?.slotStart || null);
          setSlotEndOverride(match?.slotEnd || null);
        }*/
        const slotStartZdt = startZdt || availability.startDateTime;
        if (slotStartZdt) {
          const iso = String(slotStartZdt).split("[")[0];
          const d = new Date(iso);
          const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

          const slots = await getSlotsByDate(Number(providerId), {
            date,
            zone,
          });
          const match = slots.find((s) =>
            sameInstant(s.slotStart, slotStartZdt),
          );

          setSlotInfo(match || null);
          setSlotStartOverride(match?.slotStart || null);
          setSlotEndOverride(match?.slotEnd || null);
        } else {
          setSlotStartOverride(null);
          setSlotEndOverride(null);
        }
      } catch {
        setSlotStartOverride(null);
        setSlotEndOverride(null);
      }
    } catch (e) {
      setErr(e?.message || "Failed to load appointment info");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (providerId && (availabilityId || startZdt)) fetchInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId, availabilityId, startZdt]);

  //Load insurance
  // Load insurance plans + prefill existing patient insurance (if any)
  useEffect(() => {
    let cancelled = false;
    async function loadInsurance() {
      try {
        setInsLoading(true);
        setInsError("");

        // 1) Plans for dropdowns
        const planList = await getInsurancePlans();
        if (!cancelled) setPlans(Array.isArray(planList) ? planList : []);

        // 2) Prefill patient’s latest/first insurance (if route param available)
        if (patientId) {
          const all = await listPatientInsurances(patientId);
          const rows = Array.isArray(all) ? all : [];
          if (rows.length > 0 && !cancelled) {
            // pick the most recently updated or simply first
            const pick = rows
              .slice()
              .sort(
                (a, b) =>
                  new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0),
              )[0];

            setPlanId(pick.planId || "");
            setMemberId(pick.memberId || "");
            setGroupId(pick.groupId || "");
            setCoverageStart(pick.coverageStart || "");
            setCoverageEnd(pick.coverageEnd || "");
            setStatus(pick.status || "PENDING");
          }
        }
      } catch (e) {
        if (!cancelled)
          setInsError(e?.message || "Failed to load insurance info");
      } finally {
        if (!cancelled) setInsLoading(false);
      }
    }
    loadInsurance();
    return () => {
      cancelled = true;
    };
  }, [patientId]);

  // ---- helper: ensure insurance row exists if using insurance ----
  async function ensureInsuranceForBooking() {
    // If user chose self-pay, return null memberId
    if (!useInsurance) return null;

    // If a memberId is entered, see if it already exists for this patient.
    if (memberId?.trim()) {
      const rows = await listPatientInsurances(Number(patientId));
      const exists = (rows || []).some(
        (r) => String(r.memberId).trim() === memberId.trim(),
      );
      if (exists) {
        return memberId.trim(); // use existing
      }
      // Otherwise create a new row (planId required in this path)
      if (!planId) {
        throw new Error("Please choose an Insurance Plan before booking.");
      }
      const dto = {
        id: null,
        patientId: Number(patientId),
        planId,
        memberId: memberId.trim(),
        groupId: groupId || null,
        coverageStart: coverageStart || null,
        coverageEnd: coverageEnd || null,
        category: null,
        status: status || "PENDING",
        createdAt: null,
        updatedAt: null,
      };
      const created = await createPatientInsurance(Number(patientId), dto);
      return created?.memberId || memberId.trim();
    }

    // If using insurance but no memberId entered, block
    throw new Error("Member ID is required when paying with insurance.");
  }

  //helper function that has existing booking logic
  /*const handleBookAppointmentCore = async () => {
    if (!patientId) {
      alert("Missing patient ID.");
      return;
    }
    if (!info && !startZdt) {
      alert("Missing slot details.");
      return;
    }
    if (!appointmentReason.trim()) {
      alert("Appointment reason is required.");
      return;
    }

    // Prefer the exact start we navigated with; otherwise fall back to API payload's start
    const slotStart = startZdt || info.startDateTime;


    // (Optional) guard: must be FREE to book
    if (info?.status && info.status !== "FREE") {
      if (
        !window.confirm(
          `This slot is "${info.status}". Attempt booking anyway?`,
        )
      )
        return;
    }

    // 🔹 Ensure insurance exists (or self-pay); returns memberId or null
    const resolvedMemberId = await ensureInsuranceForBooking();


    // 🔹 Call booking API (providerId first arg, payload second)
    await bookAppointmentFromSlot({
      providerId: Number(providerId),
      slotStart,
      patientId: Number(patientId),
      appointmentReason: appointmentReason.trim(),
      memberId: resolvedMemberId ?? undefined,
    });

    alert("Appointment booked successfully.");
    navigate(
      `/provider/${encodeURIComponent(providerId)}/appointments?tab=scheduled`,
      { replace: true },
    );
  };*/

  const handleBookAppointmentCore = async () => {
    if (!patientId) return alert("Missing patient ID.");
    if (!startZdt && !info?.startDateTime)
      return alert("Missing slot details.");
    if (!appointmentReason.trim())
      return alert("Appointment reason is required.");

    const selectedStart = startZdt || info.startDateTime;

    // ✅ source of truth: slot row
    const slot = await resolveSlotOrThrow({
      providerId,
      slotStartZdt: selectedStart,
      zone,
    });

    // ✅ guard: must be FREE (slot-level, not availability-level)
    if (slot.status !== "FREE") {
      alert(`That slot is ${slot.status}. Please choose another time.`);
      return;
    }

    // ✅ (optional) keep UI in sync
    setSlotStartOverride(slot.slotStart);
    setSlotEndOverride(slot.slotEnd);

    // ✅ Conflict check before booking
    const conflicts = await checkAppointmentConflict(
      patientId,
      slot.slotStart,
      slot.slotEnd,
    );
    if (conflicts.length > 0) {
      setConflictData({ conflicts, pendingSlot: slot });
      return; // pause — show conflict modal
    }

    await finishBooking(slot);
  };

  const finishBooking = async (slot) => {
    const resolvedMemberId = await ensureInsuranceForBooking();

    const booked = await bookAppointmentFromSlot({
      providerId: Number(providerId),
      slotStart: slot.slotStart,
      patientId: Number(patientId),
      appointmentReason: appointmentReason.trim(),
      memberId: resolvedMemberId ?? undefined,
    });

    // Self-pay + pricing exists → redirect to Stripe deposit
    if (!useInsurance && booked?.appointmentId) {
      const slotType = info?.appointmentType ?? slot.appointmentType;
      const matchedPricing = pricingRows.find(
        (r) => r.appointmentType === slotType,
      );
      if (matchedPricing) {
        try {
          setDepositPending(true);
          const { checkoutUrl } = await createDepositSession(
            booked.appointmentId,
          );
          window.location.href = checkoutUrl;
          return;
        } catch {
          // fall through to normal confirmation if deposit session fails
        } finally {
          setDepositPending(false);
        }
      }
    }

    alert("Appointment booked successfully.");

    const bookedDate = slot.slotStart.split("T")[0];
    navigate(
      `/patient/${encodeURIComponent(patientId)}/appointments?date=${encodeURIComponent(bookedDate)}&tab=scheduled`,
      { replace: true },
    );
  };

  //Gated click handler
  const handleBookAppointmentClick = async () => {
    // If not logged in: you can either alert or send them to login.
    // For now, keep it simple.
    if (!currentUser) {
      alert("Please sign in before booking an appointment.");
      return;
    }

    // BASIC_ACCOUNT -> open the Complete Profile modal
    if (currentUser.accountPhase === "BASIC_ACCOUNT") {
      setShowCompleteProfileModal(true);
      return;
    }

    // COMPLETED_PROFILE -> proceed with the original booking logic
    await handleBookAppointmentCore();
  };

  return (
    <>
      {conflictData && (
        <AppointmentConflictModal
          conflicts={conflictData.conflicts}
          patientId={Number(patientId)}
          proceeding={proceedingAfterConflict}
          onGoBack={() => setConflictData(null)}
          onCancelAndProceed={async () => {
            setProceedingAfterConflict(true);
            try {
              await finishBooking(conflictData.pendingSlot);
            } finally {
              setProceedingAfterConflict(false);
              setConflictData(null);
            }
          }}
        />
      )}

      <CompletePatientProfileModal
        isOpen={showCompleteProfileModal}
        onClose={() => setShowCompleteProfileModal(false)}
        token={token}
        currentUser={currentUser}
        onCompleted={(result) => {
          // Capture patientId from the create-account response (or keep existing)
          const newPatientId = result?.patientId ?? result?.id ?? null;
          if (newPatientId) setResolvedPatientId(newPatientId);
          setCurrentUser((prev) =>
            prev
              ? {
                  ...prev,
                  accountPhase: "COMPLETED_PROFILE",
                  patientId: newPatientId ?? prev.patientId,
                }
              : prev,
          );
          setShowCompleteProfileModal(false);
        }}
      />

      <div className="max-w-3xl p-4 space-y-4">
        <button
          onClick={() => window.history.back()}
          className="mb-4 text-sm px-3 py-1.5 rounded bg-gray-200 hover:bg-gray-300 flex items-center gap-1"
        >
          ← Back
        </button>

        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold">Book Appointment</h3>
          <div className="text-xs text-gray-500">{zone}</div>
        </div>

        <div className="flex gap-2">
          <Link
            to={`/provider/${encodeURIComponent(providerId)}`}
            className="text-sm px-3 py-1.5 rounded bg-gray-200 hover:bg-gray-300"
          >
            ← Provider Profile
          </Link>
          <button
            onClick={fetchInfo}
            className="text-sm px-3 py-1.5 rounded bg-gray-800 text-white hover:bg-black"
          >
            Refresh
          </button>
        </div>

        {/* Provider summary (from params) */}
        <div className="p-4 border rounded bg-white space-y-1">
          <div className="text-lg font-semibold">
            {providerName || "Provider"}
          </div>
          {specialty && (
            <div className="text-sm text-gray-700">{specialty}</div>
          )}
          {facility && <div className="text-sm text-gray-600">{facility}</div>}
        </div>

        {loading && <p className="text-gray-600">Loading…</p>}
        {err && !loading && (
          <div className="p-3 rounded bg-red-50 text-red-700 border border-red-200">
            {err}
          </div>
        )}

        {info && !loading && !err && (
          <>
            <div className="p-4 border rounded bg-white space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <div className="text-xs uppercase text-gray-500">Date</div>
                  <div className="text-base font-medium">
                    {fmtDate(info.date)}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase text-gray-500">
                    Telehealth
                  </div>
                  <div className="text-base font-medium">
                    {info.telehealth ? "Yes" : "No"}
                  </div>
                </div>

                <div>
                  <div className="text-xs uppercase text-gray-500">
                    Start time
                  </div>
                  <div className="text-base font-medium">
                    {fmtTime(
                      slotStartOverride || startZdt || info.startDateTime,
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase text-gray-500">
                    End time
                  </div>
                  <div className="text-base font-medium">
                    {fmtTime(slotEndOverride || info.endDateTime)}
                  </div>
                </div>

                <div>
                  <div className="text-xs uppercase text-gray-500">Status</div>
                  <span
                    className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
                      info.status === "FREE"
                        ? "bg-green-100 text-green-700"
                        : info.status === "PAST"
                          ? "bg-gray-100 text-gray-700"
                          : info.status === "BOOKED"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-yellow-100 text-yellow-700"
                    }`}
                  >
                    {slotInfo?.status ?? info.status}
                  </span>
                </div>
              </div>
            </div>

            {/* Insurance section */}
            <div className="p-4 border rounded bg-white space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-lg font-semibold">Insurance</div>
                <div className="text-xs text-gray-500">
                  {insLoading ? (
                    "Loading insurance…"
                  ) : insError ? (
                    <span className="text-red-600">{insError}</span>
                  ) : null}
                </div>
              </div>

              {/* Collapsible toggles */}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setUseInsurance(true)}
                  disabled={acceptingInsurance !== true}
                  className={`px-3 py-1.5 rounded disabled:opacity-50 disabled:cursor-not-allowed ${useInsurance ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-800"}`}
                >
                  Pay with Insurance
                </button>
                <button
                  type="button"
                  onClick={() => setUseInsurance(false)}
                  className={`px-3 py-1.5 rounded ${!useInsurance ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-800"}`}
                >
                  Pay without Insurance
                </button>
              </div>

              {acceptingInsurance === false && (
                <p className="text-sm text-amber-700">
                  Provider accepts self-pay only.
                </p>
              )}
              {insuranceSettingError && (
                <p className="text-sm text-red-600">{insuranceSettingError}</p>
              )}

              {/* Without insurance — pricing breakdown if available */}
              {!useInsurance &&
                (() => {
                  const matched = matchedPricing;
                  if (matched) {
                    return (
                      <div className="p-4 rounded-lg bg-blue-50 border border-blue-200 text-sm space-y-2">
                        <p className="font-semibold text-blue-800">
                          Self-Pay Pricing
                        </p>
                        <div className="flex justify-between text-blue-700">
                          <span>Full appointment cost</span>
                          <span>${Number(matched.fullPrice).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-blue-700 font-medium">
                          <span>Deposit due now (via Stripe)</span>
                          <span>
                            ${Number(matched.depositAmount).toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between text-blue-600">
                          <span>Remaining (pay at visit)</span>
                          <span>
                            $
                            {(
                              Number(matched.fullPrice) -
                              Number(matched.depositAmount)
                            ).toFixed(2)}
                          </span>
                        </div>
                        <p className="text-xs text-blue-500 pt-1">
                          Deposit is refundable if cancelled more than 24 hours
                          before your appointment.
                        </p>
                      </div>
                    );
                  }
                  return acceptingInsurance === false ? (
                    <div className="p-3 rounded bg-red-50 text-red-700 border border-red-200 text-sm">
                      Self-pay pricing is unavailable for this appointment type.
                    </div>
                  ) : (
                    <div className="p-3 rounded bg-amber-50 text-amber-800 border border-amber-200 text-sm">
                      Cost will be out of pocket.
                    </div>
                  );
                })()}

              {/* With insurance form */}
              {useInsurance && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Plan */}
                  <div>
                    <label className="block mb-1 text-sm font-medium">
                      Insurance Plan <span className="text-red-600">*</span>
                    </label>
                    <select
                      value={planId}
                      onChange={(e) => {
                        setPlanId(e.target.value);
                        setPlanTypeId("");
                      }}
                      className="w-full border rounded px-3 py-2"
                    >
                      <option value="">Select a plan…</option>
                      {plans.map((p) => (
                        <option key={p.planId} value={p.planId}>
                          {p.planName} ({p.planId})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Plan Type (from selected plan) */}
                  <div>
                    <label className="block mb-1 text-sm font-medium">
                      Plan Type <span className="text-red-600">*</span>
                    </label>
                    <select
                      value={planTypeId}
                      onChange={(e) => setPlanTypeId(e.target.value)}
                      className="w-full border rounded px-3 py-2"
                      disabled={!planId || planTypeOptions.length === 0}
                    >
                      <option value="">Select type…</option>
                      {planTypeOptions.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.typeName}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Member ID */}
                  <div>
                    <label className="block mb-1 text-sm font-medium">
                      Member ID <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      value={memberId}
                      onChange={(e) => setMemberId(e.target.value)}
                      className="w-full border rounded px-3 py-2"
                      placeholder="e.g., MEM-001"
                    />
                  </div>

                  {/* Group ID */}
                  <div>
                    <label className="block mb-1 text-sm font-medium">
                      Group ID (optional)
                    </label>
                    <input
                      type="text"
                      value={groupId}
                      onChange={(e) => setGroupId(e.target.value)}
                      className="w-full border rounded px-3 py-2"
                      placeholder="e.g., GRP-123"
                    />
                  </div>

                  {/* Coverage Start */}
                  <div>
                    <label className="block mb-1 text-sm font-medium">
                      Coverage Start
                    </label>
                    <input
                      type="date"
                      value={coverageStart || ""}
                      onChange={(e) => setCoverageStart(e.target.value)}
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>

                  {/* Coverage End */}
                  <div>
                    <label className="block mb-1 text-sm font-medium">
                      Coverage End
                    </label>
                    <input
                      type="date"
                      value={coverageEnd || ""}
                      onChange={(e) => setCoverageEnd(e.target.value)}
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>

                  {/* Status */}
                  <div>
                    <label className="block mb-1 text-sm font-medium">
                      Status
                    </label>
                    <select
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                      className="w-full border rounded px-3 py-2"
                    >
                      <option value="PENDING">PENDING</option>
                      <option value="VERIFIED">VERIFIED</option>
                      <option value="REJECTED">REJECTED</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* Appointment Reason */}
            <div className="p-4 border rounded bg-white space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Appointment Reason <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  value={appointmentReason}
                  onChange={(e) => setAppointmentReason(e.target.value)}
                  placeholder="e.g. Follow-up on MRI"
                  className="w-full border rounded px-3 py-2"
                />
              </div>
            </div>

            {/* Book Appointment */}
            <div className="flex gap-2">
              <button
                disabled={!canBook || depositPending}
                className={`px-4 py-2 rounded text-white ${
                  canBook && !depositPending
                    ? "bg-blue-600 hover:bg-blue-700"
                    : "bg-gray-400 cursor-not-allowed"
                }`}
                onClick={handleBookAppointmentClick}
              >
                {depositPending
                  ? "Redirecting to payment..."
                  : !useInsurance &&
                      pricingRows.find(
                        (r) => r.appointmentType === info?.appointmentType,
                      )
                    ? "Pay Deposit & Book"
                    : "Book Appointment"}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
